"use client"

import { useState } from "react"
import { useParams } from "next/navigation"
import { useAiChat, normalizeLocale } from "@lib/hooks/use-ai-chat"
import { AiContent } from "@lib/util/ai-content"
import { useT } from "@lib/i18n/use-t"

export default function AiSection() {
  const params = useParams()
  const locale = normalizeLocale(params?.countryCode as string | undefined)
  const [input, setInput] = useState("")
  const { messages, loading, error, retryAfter, send } = useAiChat({ locale })
  const t = useT()

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const text = input
    setInput("")
    await send(text)
  }

  const lastUser = [...messages].reverse().find((m) => m.role === "user")
  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant")

  return (
    <section
      id="ai-section"
      className="border-y border-slate-200/60 bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 dark:border-white/8 dark:from-[#0f172a] dark:via-[#111827] dark:to-[#0f172a]"
    >
      <div className="content-container grid items-center gap-12 py-16 md:grid-cols-2 md:gap-14 md:py-20">
        {/* Left — text */}
        <div>
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand-light/15 bg-brand-light/5 px-3 py-1 dark:border-brand-dark/15 dark:bg-brand-dark/8">
            <span className="text-sm">🤖</span>
            <span className="text-[11px] font-semibold tracking-wider text-brand-light dark:text-brand-dark">
              {t("poweredByAi")}
            </span>
          </div>

          <h2 className="font-display text-3xl font-bold leading-tight text-slate-900 dark:text-[#e8f0ff]">
            {t("aiConsultantTitle")}{" "}
            <span className="text-brand-light dark:text-brand-dark">
              Kvatronix
            </span>
          </h2>

          <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
            {t("aiConsultantDesc")}
          </p>

          <div className="mt-6 flex flex-col gap-3">
            {[
              t("aiFeature1"),
              t("aiFeature2"),
              t("aiFeature3"),
              t("aiFeature4"),
            ].map((f) => (
              <div
                key={f}
                className="flex items-center gap-2.5 text-sm text-slate-700 dark:text-slate-300"
              >
                <span className="h-1 w-1 shrink-0 rounded-full bg-brand-light dark:bg-brand-dark" />
                {f}
              </div>
            ))}
          </div>
        </div>

        {/* Right — live chat */}
        <div className="overflow-hidden rounded-2xl border border-slate-200/60 bg-white shadow-lg dark:border-white/8 dark:bg-[#0a0e1a]">
          {/* Header */}
          <div className="flex items-center gap-3 border-b border-slate-100 px-5 py-3 dark:border-white/5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-light/10 dark:bg-brand-dark/10">
              <img src="/logo-mark.svg" alt="" className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900 dark:text-white">
                {t("aiAssistantName")}
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-emerald-600 dark:text-emerald-400">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {t("online")}
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex min-h-[220px] flex-col gap-3 p-4">
            {!lastUser && !loading && (
              <>
                <div className="max-w-[90%] self-start rounded-2xl rounded-bl-sm bg-slate-100 px-4 py-3 text-[13px] leading-relaxed dark:bg-[#1e293b]">
                  {t("aiGreeting")}
                </div>
                <div className="flex flex-wrap gap-2">
                  {[t("aiChip1"), t("aiChip2"), t("aiChip3")].map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setInput(c)}
                      className="rounded-full border border-brand-light/25 bg-brand-light/5 px-3 py-1.5 text-[12px] text-brand-light transition hover:bg-brand-light/10 dark:border-brand-dark/20 dark:bg-brand-dark/8 dark:text-brand-dark dark:hover:bg-brand-dark/12"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </>
            )}
            {lastUser && (
              <div className="max-w-[85%] self-end rounded-2xl rounded-br-sm border border-brand-light/15 bg-brand-light/5 px-4 py-2.5 text-[13px] leading-relaxed dark:border-brand-dark/15 dark:bg-brand-dark/8">
                {lastUser.content}
              </div>
            )}
            {lastAssistant && (
              <div className="max-w-[90%] self-start rounded-2xl rounded-bl-sm bg-slate-100 px-4 py-3 text-[13px] leading-relaxed whitespace-pre-wrap dark:bg-[#1e293b]">
                <AiContent text={lastAssistant.content} />
              </div>
            )}
            {loading && (
              <div className="flex items-center gap-1.5 self-start rounded-2xl rounded-bl-sm bg-slate-100 px-4 py-3 dark:bg-[#1e293b]">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.3s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.15s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" />
              </div>
            )}
          </div>

          {(error || retryAfter > 0) && (
            <div className="border-t border-amber-200/60 bg-amber-50 px-4 py-2 text-[12px] text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
              {retryAfter > 0
                ? `${t("aiRateLimitPrefix")} ${retryAfter}${t("aiRateLimitSuffix")}`
                : error}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={onSubmit}
            className="relative flex gap-2 border-t border-slate-100 p-3 dark:border-white/5"
          >
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("aiInputPlaceholder")}
              disabled={loading || retryAfter > 0}
              className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-brand-light disabled:opacity-50 dark:border-white/10 dark:bg-[#111827] dark:text-white dark:focus:border-brand-dark"
            />
            <button
              type="submit"
              disabled={!input.trim() || loading || retryAfter > 0}
              aria-label={t("aiSendAria")}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-r from-brand-light to-brand-600 text-white transition hover:opacity-90 disabled:opacity-40 dark:from-brand-dark dark:to-brand-500"
            >
              ↑
            </button>
          </form>
        </div>
      </div>
    </section>
  )
}
